import { CallHandler, ExecutionContext, Injectable, NestInterceptor } from '@nestjs/common';
import { PrismaService } from '@cart/api-data-access';
import { Request } from 'express';
import { Observable, from, lastValueFrom } from 'rxjs';
import { AuditContextService } from './audit-context.service';

/**
 * Only these prefixes. Deliberately NOT the storefront or checkout.
 *
 * Wrapping a request in a transaction changes its failure semantics — partial
 * work now rolls back — and holds a connection for the request's lifetime. On
 * back-office routes that is a fair trade and arguably more correct: they are
 * low-volume, already the slow path, and a half-applied product edit is a bug.
 * On checkout it is not a trade worth making blind, so that path keeps today's
 * behaviour and gets its attribution from the explicit `publishActor` calls
 * already inside its own transactions.
 */
const TRANSACTIONAL_PREFIXES = ['/api/admin/', '/api/merchant/', '/api/platform/'];

/**
 * Routes that must never be wrapped, despite matching a prefix above.
 *
 * These do long external I/O — pg_dump, pg_basebackup, uploads to R2, starting
 * a second PostgreSQL — and take tens of seconds. Wrapping them achieved
 * nothing and broke them:
 *
 *   - A database transaction around external side effects gives neither
 *     atomicity nor isolation. Rolling back the row does not un-upload a 95 MB
 *     object, so the "safety" was illusory.
 *   - It pinned a pooled connection for the whole operation.
 *   - It exceeded Prisma's 30s interactive-transaction timeout and failed the
 *     request outright (P2028), *after* the backup had already succeeded — so
 *     the object existed in R2 while the run row said failed.
 *
 * Same reasoning as the multipart exclusion below, which was already here for
 * uploads; this is the same class of work.
 */
const NON_TRANSACTIONAL_PREFIXES = ['/api/platform/database/'];

const READ_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

@Injectable()
export class AuditTransactionInterceptor implements NestInterceptor {
  constructor(
    private readonly prisma: PrismaService,
    private readonly auditContext: AuditContextService
  ) {}

  intercept(context: ExecutionContext, next: CallHandler): Observable<unknown> {
    if (context.getType() !== 'http') return next.handle();

    const request = context.switchToHttp().getRequest<Request>();
    if (!this.shouldWrap(request)) return next.handle();

    const actor = this.auditContext.actorPayload();
    if (!actor) return next.handle();

    // Subscribing to next.handle() INSIDE runInTransaction is what puts the
    // route handler in the transaction's async context, so every
    // `this.prisma.*` call it makes joins that transaction and every row its
    // triggers write carries this actor.
    return from(
      this.prisma.runInTransaction(
        () => lastValueFrom(next.handle(), { defaultValue: undefined }),
        {
          onOpen: async (tx) => {
            // Transaction-local. See PrismaService.txStorage for why it cannot
            // be session-level.
            await tx.$executeRaw`SELECT set_config('audit.actor', ${actor}, true)`;
          },
        }
      )
    );
  }

  private shouldWrap(request: Request): boolean {
    if (process.env.AUDIT_REQUEST_TX === 'off') return false;
    if (READ_METHODS.has(request.method)) return false;

    const path = request.originalUrl ?? request.url ?? '';
    if (!TRANSACTIONAL_PREFIXES.some((prefix) => path.startsWith(prefix))) return false;
    if (NON_TRANSACTIONAL_PREFIXES.some((prefix) => path.startsWith(prefix))) return false;

    // Uploads process images and push to object storage before returning.
    // Holding a database transaction open across that would pin a connection
    // for seconds on work that touches no rows until the very end.
    const contentType = request.headers['content-type'] ?? '';
    if (contentType.includes('multipart/form-data')) return false;

    return true;
  }
}
